import { RegularMetricId } from '../../../../api/types-graphql';
import {
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
} from '../../../../common/components/data-viz-containers/common/types';
import { KpiFactboxSingleMetricVizViews } from '../../../../common/components/data-viz-containers/kpi-factbox-single-metric/types';
import { KpiFactboxTwoMetricVizViews } from '../../../../common/components/data-viz-containers/kpi-factbox-two-metric/types';
import {
  StoryboardComponentIds,
  StoryboardItemConfig,
} from '../../../../common/components/storyboard-components/common/types';
import { Languages } from '../../../../constants';
import {
  CalendarYearSingleValueByMonthsTimeSelection,
  DataTypes,
  EmployeeDataFields,
} from '../../../../types';

export const getIndividualsFactbox = (
  currentMonthTimeSelection: CalendarYearSingleValueByMonthsTimeSelection
): StoryboardItemConfig => {
  return {
    chartId: '1',
    layoutConfig: {
      gridColSpan: 5,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Individuals',
          [Languages.JA]: '従業員数',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_2IndividualsActiveInPeriodCount,
            },
            timeSelection: currentMonthTimeSelection,
            filters: `EMPLOYEE.ORGANIZATION_LEVEL_1 = 'グローバル' 
                  OR EMPLOYEE.ORGANIZATION_LEVEL_1 = 'ツーリズム' 
                  OR EMPLOYEE.ORGANIZATION_LEVEL_1 = 'エリアソリューション' 
                  OR EMPLOYEE.ORGANIZATION_LEVEL_1 = 'ビジネスソリューション' 
                  OR EMPLOYEE.ORGANIZATION_LEVEL_1 = '事業基盤機能' 
                  OR EMPLOYEE.ORGANIZATION_LEVEL_1 = '休職者'`,
            segmentBy: {
              type: 'non-hierarchical',
              dataField: {
                dataType: DataTypes.EMPLOYEE,
                dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
              },
              filters: [
                'グローバル',
                'ツーリズム',
                'エリアソリューション',
                'ビジネスソリューション',
                '事業基盤機能',
                '休職者',
              ],
            },
          },
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getIndividualsSecondedFactbox = (
  currentMonthTimeSelection: CalendarYearSingleValueByMonthsTimeSelection
): StoryboardItemConfig => {
  return {
    chartId: '2',
    layoutConfig: {
      gridColSpan: 5,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Individuals',
          [Languages.JA]: '従業員数',
        },
        subtitle: {
          [Languages.EN]: '(Seconded employees)',
          [Languages.JA]: '(出向者)',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_2IndividualsActiveInPeriodCount,
            },
            timeSelection: currentMonthTimeSelection,
            filters: `EMPLOYEE.ORGANIZATION_LEVEL_1 = '出向者'`,
          },
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getJoinersFactbox = (
  currentMonthTimeSelection: CalendarYearSingleValueByMonthsTimeSelection,
  fyYtdToCurrentMonthTimeSelection: CalendarYearSingleValueByMonthsTimeSelection,
  customField1Filter: string
): StoryboardItemConfig => {
  return {
    chartId: '3',
    layoutConfig: {
      gridColSpan: 5,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_TWO_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: '# of Joiners',
          [Languages.JA]: '入社者',
        },
        subtitle: {
          [Languages.EN]: '(Career + New Graduates)',
          [Languages.JA]: '(キャリア+新卒)',
        },
        sections: {
          leftSection: {
            title: {
              [Languages.EN]: '(Most recent month)',
              [Languages.JA]: '(最新の月間値)',
            },
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0006_1JoinersJoiners,
              },
              timeSelection: currentMonthTimeSelection,
              filters: customField1Filter,
            },
            benchmarkConfigs: [
              {
                benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
                benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
              },
            ],
          },
          rightSection: {
            title: {
              [Languages.EN]: '(FYTD)',
              [Languages.JA]: '(期初からの累計)',
            },
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0006_1JoinersJoiners,
              },
              timeSelection: fyYtdToCurrentMonthTimeSelection,
              filters: customField1Filter,
            },
            benchmarkConfigs: [
              {
                benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
                benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              },
            ],
          },
        },
      },
      selectedView: KpiFactboxTwoMetricVizViews.FACTBOX,
    },
  };
};

export const getVoluntaryAttritionFactbox = (
  fyYtdToCurrentMonthTimeSelection: CalendarYearSingleValueByMonthsTimeSelection
): StoryboardItemConfig => {
  return {
    chartId: '4',
    layoutConfig: {
      gridColSpan: 5,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Voluntary Attrition',
          [Languages.JA]: '自己都合離職者',
        },
        subtitle: {
          [Languages.EN]: '(FYTD)',
          [Languages.JA]: '(期初からの累計)',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0237_4VoluntaryAttritionLeavers,
            },
            timeSelection: fyYtdToCurrentMonthTimeSelection,
          },
          benchmarkConfigs: [
            {
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getFemaleHeadcountFactbox = (
  currentMonthTimeSelection: CalendarYearSingleValueByMonthsTimeSelection
): StoryboardItemConfig => {
  return {
    chartId: '5',
    layoutConfig: {
      gridColSpan: 4,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Female Headcount',
          [Languages.JA]: '女性従業員',
        },
        subtitle: {
          [Languages.EN]: '(Most recent month)',
          [Languages.JA]: '(最新の月間値)',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0020_2FhcPercHc,
            },
            timeSelection: currentMonthTimeSelection,
          },
          benchmarkConfigs: [
            {
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getFemaleManagersFactbox = (
  currentMonthTimeSelection: CalendarYearSingleValueByMonthsTimeSelection
): StoryboardItemConfig => {
  return {
    chartId: '6',
    layoutConfig: {
      gridColSpan: 4,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Female Managers (Defined)',
          [Languages.JA]: '女性管理職',
        },
        subtitle: {
          [Languages.EN]: '(Most recent month)',
          [Languages.JA]: '(最新の月間値)',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0016_3FMngDefPercMng,
            },
            timeSelection: currentMonthTimeSelection,
          },
          benchmarkConfigs: [
            {
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getAvgWorkHoursFactbox = (
  twoMonthsAgoTimeSelection: CalendarYearSingleValueByMonthsTimeSelection
): StoryboardItemConfig => {
  return {
    chartId: '7',
    layoutConfig: {
      gridColSpan: 4,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Avg Work Hours per Person',
          [Languages.JA]: '総労働時間',
        },
        subtitle: {
          [Languages.EN]: '(2 months ago)',
          [Languages.JA]: '(前々月の値)',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0213_2AvgWorkHoursPerPersonAvgWithWh,
            },
            timeSelection: twoMonthsAgoTimeSelection,
            segmentBy: {
              type: 'non-hierarchical',
              dataField: {
                dataType: DataTypes.EMPLOYEE,
                dataField: EmployeeDataFields.CUSTOM_FIELD_3_LEVEL_1,
              },
              filters: ['管理監督者', '管理監督者以外'],
            },
          },
          benchmarkConfigs: [
            {
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getAvgOvertimeFactbox = (
  twoMonthsAgoTimeSelection: CalendarYearSingleValueByMonthsTimeSelection
): StoryboardItemConfig => {
  return {
    chartId: '8',
    layoutConfig: {
      gridColSpan: 4,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Avg Overtime per Employee',
          [Languages.JA]: '所定外労働時間',
        },
        subtitle: {
          [Languages.EN]: '(2 months ago)',
          [Languages.JA]: '(前々月の値)',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0215_2MonthlyOtSumPerHcWithOt,
            },
            timeSelection: twoMonthsAgoTimeSelection,
          },
          benchmarkConfigs: [
            {
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getPaidLeaveFactbox = (
  fyYtdToTwoMonthsAgoTimeSelection: CalendarYearSingleValueByMonthsTimeSelection
): StoryboardItemConfig => {
  return {
    chartId: '9',
    layoutConfig: {
      gridColSpan: 4,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Statutory Annual Paid Leave',
          [Languages.JA]: '有給取得日数',
        },
        subtitle: {
          [Languages.EN]: '(Cumulative Val till 2 months ago)',
          [Languages.JA]: '(期初から前々月の累計値)',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0381_2StryAnlPaidLeavenotempCumSumPerHc,
            },
            timeSelection: fyYtdToTwoMonthsAgoTimeSelection,
          },
          benchmarkConfigs: [
            {
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};
